import { useCallback, type FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import {
  type ComponentMetadata,
  type ParameterDataType
} from 'api/hooks/useGetComponentMetadata/types'
import { type ComponentSummaryId } from 'api/hooks/useGetComponentSummaries'
import { type EditorColumn } from 'api/hooks/useGetParameterOptions/types'

import { useFlags } from 'hooks/useFlags'

import { jobActions } from 'job-lib/store'
import { isDPLParameterCollection } from 'job-lib/store/jobSlice/utils/isDPLParameterCollection'
import { type ComponentInstance } from 'job-lib/types/Job'
import { type ElementCollection } from 'job-lib/types/Parameters'

import { useComponentValidationProvider } from 'modules/core/ComponentValidation'

import classes from './ComponentParameters.module.scss'
import { ComponentParametersGrid } from './ComponentParametersGrid'
import { ComponentParametersWizard } from './ComponentParametersWizard'
import { convertElementCollectionToValue } from './utils/convertElementCollectionToValue'

interface ComponentParametersProps {
  componentInstance: ComponentInstance
  componentMetadata: ComponentMetadata
  componentSummaryId: ComponentSummaryId
}

export interface EditedParameter {
  parameterSlot: number
  parameterPath: string[]
  parameterDataType: ParameterDataType
  editedValue: ElementCollection
  editorColumns?: EditorColumn[]
}

export const ComponentParametersContainer: FC<ComponentParametersProps> = ({
  componentInstance,
  componentMetadata,
  componentSummaryId
}) => {
  const { enableWizardUiForComponents } = useFlags()

  const { t } = useTranslation()

  const dispatch = useDispatch()
  const { invalidateComponent } = useComponentValidationProvider()

  const onEdit = useCallback(
    ({
      parameterSlot,
      parameterPath,
      parameterDataType,
      editedValue,
      editorColumns
    }: EditedParameter) => {
      const { parameters } = componentInstance
      const parameterValue = isDPLParameterCollection(parameters)
        ? convertElementCollectionToValue(
            editedValue,
            parameterDataType,
            editorColumns
          )
        : editedValue

      dispatch(
        jobActions.overrideComponentParameter({
          componentMetadata,
          parameterSlot,
          parameterPath,
          componentInstanceId: componentInstance.id,
          value: parameterValue
        })
      )

      invalidateComponent(componentInstance.id)
    },
    [componentInstance, componentMetadata, dispatch, invalidateComponent]
  )

  return (
    <div
      className={classes.ComponentParameters}
      aria-label={t('translation:componentProperties.tableTitle')}
      data-testid="component-parameter-editor"
      role="table"
    >
      {enableWizardUiForComponents ? (
        <ComponentParametersWizard
          componentInstance={componentInstance}
          componentSummaryId={componentSummaryId}
          componentMetadata={componentMetadata}
          onEdit={onEdit}
        />
      ) : (
        <ComponentParametersGrid
          componentInstance={componentInstance}
          componentSummaryId={componentSummaryId}
          componentMetadata={componentMetadata}
          onEdit={onEdit}
        />
      )}
    </div>
  )
}
